import React from "react";
import { motion } from "framer-motion";
import { palette } from "../palette";
import { GrainDefs, GrainOverlay } from "./Grain";
import { useReducedMotion } from "../motion";

const c = palette.ill;

// Shared shell: rounded panel, tinted ground, grain on top.
const Panel = ({ id, w, h, bg, label, children, className }) => {
  const reduced = useReducedMotion();
  return (
    <motion.svg
      className={className}
      viewBox={`0 0 ${w} ${h}`}
      width="100%"
      role="img"
      aria-label={label}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
      initial={reduced ? false : { opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.65, ease: [0.22, 0.61, 0.36, 1] }}
    >
      <defs>
        <GrainDefs id={id} />
        <clipPath id={`${id}-clip`}>
          <rect width={w} height={h} rx="22" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${id}-clip)`}>
        <rect width={w} height={h} fill={bg} />
        {children}
        <GrainOverlay id={id} width={w} height={h} rx={22} />
      </g>
    </motion.svg>
  );
};

// GPU racks with blinking status lights — the compute we hand over.
export const GpuRack = ({ className }) => {
  const reduced = useReducedMotion();
  const racks = [0, 1, 2];

  return (
    <Panel id="gpurack" w={420} h={300} bg={c.sky} label="Racks of GPU servers with status lights blinking" className={className}>
      <rect y="236" width="420" height="64" fill={c.sand} />
      {racks.map((r) => {
        const x = 52 + r * 108;
        return (
          <g key={r}>
            <rect x={x} y="74" width="84" height="162" rx="8" fill={c.ink} />
            {[0, 1, 2, 3, 4].map((i) => (
              <g key={i}>
                <rect x={x + 10} y={88 + i * 30} width="64" height="20" rx="4" fill="#3d434e" />
                <motion.circle
                  cx={x + 20} cy={98 + i * 30} r="3.5"
                  fill={i % 2 ? c.leaf : c.river}
                  {...(reduced ? {} : {
                    animate: { opacity: [0.35, 1, 0.35] },
                    transition: { duration: 1.6 + i * 0.3, repeat: Infinity, delay: r * 0.4 },
                  })}
                />
                <rect x={x + 30} y={95 + i * 30} width="30" height="6" rx="3" fill="#5a6270" />
              </g>
            ))}
          </g>
        );
      })}
      {/* Heat/airflow drifting up */}
      {!reduced && [0, 1, 2].map((i) => (
        <motion.path
          key={i}
          d={`M${94 + i * 108} 68 q8 -16 0 -30`}
          stroke={c.river} strokeWidth="3" strokeLinecap="round" fill="none" opacity="0.35"
          animate={{ y: [0, -10, 0], opacity: [0.15, 0.4, 0.15] }}
          transition={{ duration: 3 + i, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}
    </Panel>
  );
};

// A friendly robot at a desk — the tutor that never sleeps.
export const TutorBot = ({ className }) => {
  const reduced = useReducedMotion();

  return (
    <Panel id="tutorbot" w={360} h={300} bg={c.sand} label="A friendly robot tutor beside a screen, available at any hour" className={className}>
      <circle cx="180" cy="132" r="96" fill={c.sky} opacity="0.75" />

      {/* Antenna */}
      <line x1="180" y1="62" x2="180" y2="44" stroke={c.ink} strokeWidth="5" strokeLinecap="round" />
      <motion.circle
        cx="180" cy="40" r="7" fill={c.clay}
        {...(reduced ? {} : {
          animate: { opacity: [0.5, 1, 0.5], scale: [1, 1.15, 1] },
          transition: { duration: 2.4, repeat: Infinity, ease: "easeInOut" },
        })}
        style={{ transformOrigin: "180px 40px" }}
      />

      {/* Head */}
      <rect x="128" y="62" width="104" height="84" rx="22" fill={palette.paper} stroke={c.ink} strokeWidth="5" />
      <motion.g
        {...(reduced ? {} : {
          animate: { scaleY: [1, 1, 0.1, 1] },
          transition: { duration: 4.5, repeat: Infinity, times: [0, 0.86, 0.92, 1] },
        })}
        style={{ transformOrigin: "180px 100px" }}
      >
        <circle cx="158" cy="100" r="9" fill={c.ink} />
        <circle cx="202" cy="100" r="9" fill={c.ink} />
      </motion.g>
      <path d="M163 124 q17 13 34 0" stroke={c.ink} strokeWidth="5" strokeLinecap="round" fill="none" />

      {/* Body and arms */}
      <rect x="140" y="152" width="80" height="62" rx="16" fill={c.river} />
      <rect x="164" y="168" width="32" height="10" rx="5" fill={palette.paper} opacity="0.85" />
      <path d="M140 172 q-26 8 -28 34" stroke={c.river} strokeWidth="13" strokeLinecap="round" fill="none" />
      <path d="M220 172 q26 8 28 34" stroke={c.river} strokeWidth="13" strokeLinecap="round" fill="none" />

      {/* Little chat bubbles it answers with */}
      {[0, 1].map((i) => (
        <motion.g key={i}
          {...(reduced ? {} : {
            animate: { y: [0, -8, 0], opacity: [0.7, 1, 0.7] },
            transition: { duration: 3.4 + i, repeat: Infinity, ease: "easeInOut", delay: i * 0.8 },
          })}
        >
          <rect x={i ? 250 : 48} y={i ? 118 : 86} width={i ? 62 : 56} height="34" rx="12" fill={palette.paper} opacity="0.95" />
          <rect x={i ? 260 : 58} y={i ? 130 : 98} width={i ? 30 : 26} height="4.5" rx="2.25" fill={c.skyDeep} />
          <rect x={i ? 260 : 58} y={i ? 139 : 107} width={i ? 42 : 36} height="4.5" rx="2.25" fill={c.skyDeep} />
        </motion.g>
      ))}
    </Panel>
  );
};

// A founder pitching to a small room — the other valid finish.
export const FounderPitch = ({ className }) => (
  <Panel id="pitch" w={420} h={300} bg={c.sky} label="A founder presenting a growth chart to a small audience" className={className}>
    <rect y="232" width="420" height="68" fill={c.sand} />

    {/* Screen with a rising line */}
    <rect x="150" y="48" width="230" height="146" rx="10" fill={palette.paper} stroke={c.ink} strokeWidth="5" />
    <path d="M172 166 L214 132 L252 148 L292 96 L356 72" stroke={c.river} strokeWidth="6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="356" cy="72" r="8" fill={c.clay} />
    <rect x="240" y="194" width="50" height="38" fill={c.woodDeep} />

    {/* Presenter, arm raised toward the screen */}
    <g>
      <path d="M78 232 L74 300 M98 232 L104 300" stroke={c.ink} strokeWidth="9" strokeLinecap="round" />
      <path d="M66 152 Q66 134 88 134 Q110 134 110 152 L110 236 L66 236 Z" fill={c.plum} />
      <path d="M108 160 Q140 146 152 118" stroke={c.skin[2]} strokeWidth="11" strokeLinecap="round" fill="none" />
      <circle cx="88" cy="114" r="20" fill={c.skin[2]} />
      <path d="M69 108 a20 20 0 0 1 38 -2 q-8 -12 -20 -10 t-18 12" fill={c.ink} />
    </g>

    {/* Audience, seen from behind */}
    {[
      { x: 176, shirt: c.leafDeep, skin: c.skin[1] },
      { x: 236, shirt: c.clay, skin: c.skin[0] },
      { x: 296, shirt: c.river, skin: c.skin[3] },
    ].map((a) => (
      <g key={a.x}>
        <path d={`M${a.x - 22} 300 Q${a.x - 22} 262 ${a.x} 262 Q${a.x + 22} 262 ${a.x + 22} 300 Z`} fill={a.shirt} />
        <circle cx={a.x} cy={244} r="17" fill={a.skin} />
        <circle cx={a.x} cy={240} r="18" fill={c.ink} opacity="0.85" />
        <circle cx={a.x} cy={247} r="15" fill={a.skin} />
      </g>
    ))}
  </Panel>
);

export default { GpuRack, TutorBot, FounderPitch };
